import React, { useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  message,
  Modal,
  Popover,
  Row,
  Select,
  Switch,
  Typography,
  Space,
  InputNumber,
  Empty,
  Table,
} from 'antd';
import {
  ASSET_CLASSES,
  AssetClassOptions,
  BETA_METRICS,
  CreateIndexFund,
  createIndexFund,
  createUnderlyingAsset,
  DURATION_METRICS,
  instrumentTypes,
  LeaderboardWeightsType,
  marketHours,
  METRICS_TYPES,
  RefreshAssetEod,
  refreshAssetEodFromRefinitiv,
  RefreshIndexFund,
  refreshIndexFundDataFromRefinitiv,
  REGIONS,
  RISK_WEIGHTED_APPROACH,
  selectors,
  UnderlyingAsset,
  updateAssetClassOptions,
  updateLeaderboardWeights,
  uploadBulkEodPrices,
  uploadBulkNewAssets,
} from 'state';
import { useDispatch, useSelector } from 'react-redux';
import { InfoCircleOutlined } from '@ant-design/icons';
import { API_URL } from 'settings';

type Props = {
  hide: () => void;
};
type State = {
  [metricType: number]: LeaderboardWeightsType;
};
const EditWeightsModal: React.FC<Props> = ({ hide }) => {
  const dispatch = useDispatch();

  const { asset_class_options, risk_approach, leaderboard_weights } =
    useSelector(selectors.activeSession);

  const maskedLeaderboardWeights = leaderboard_weights.filter(item => {
    return (
      item.metric_type !== DURATION_METRICS && item.metric_type !== BETA_METRICS
    );
  });
  // console.log('asset_class_options: ', asset_class_options);
  const [editing, setEditing] = useState(false);
  const [weights, setWeights] = useState<State>({});
  const [errors, setErrors] = useState<string[]>([]);

  // const playableAssetClasses_ = useSelector(selectors.playableAssetClasses());
  useEffect(() => {
    const newLimits = {} as State;
    maskedLeaderboardWeights.forEach(a => {
      newLimits[a.metric_type] = {
        metric_type: a.metric_type,
        weight: a.weight,
      };
    });
    setWeights(newLimits);
  }, [leaderboard_weights]);

  const dataSource = maskedLeaderboardWeights.map(({ ...item }) => {
    const weight = {
      metric_type: item.metric_type,
      weight: item.weight,
    };
    const name = METRICS_TYPES[item.metric_type];
    const metricType = item.metric_type;
    return { name, metricType, weight };
  });
  // .filter(assetClassOption =>
  //   playableAssetClasses_.includes(assetClassOption.assetClass)
  // );

  // console.log('datasource ACL: ', dataSource);

  const reset = () => {
    const newWeights = {} as State;
    maskedLeaderboardWeights.forEach(a => {
      newWeights[a.metric_type] = {
        metric_type: a.metric_type,
        weight: a.weight,
      };
    });
    setWeights(newWeights);
    setErrors([]);
  };

  const getProps = (field: 'weight') => {
    return {
      render(_: unknown, record: (typeof dataSource)[0]) {
        if (editing) {
          // const assetClass = record.asset_class;
          const allocation = weights[record.metricType];
          const weight = allocation?.[field] ?? 0;

          return (
            <div>
              <InputNumber
                value={weight}
                style={{ width: 70 }}
                onChange={value => {
                  if (value === undefined) {
                    return;
                  }
                  setWeights({
                    ...weights,
                    [record.metricType]: {
                      ...allocation,
                      metric_type: record.metricType,
                      [field]: value,
                    },
                  });
                }}
                step={0.01}
                min={0}
                max={1}
                formatter={value => {
                  const nr = Number(value) || 0;
                  return `${Math.round(nr * 100)}%`;
                }}
                parser={value => {
                  const parsedValue = `${value?.replaceAll('%', '')}`;
                  const nr = Number(parsedValue);
                  if (!isNaN(nr)) {
                    return Number((nr / 100).toFixed(2));
                  }
                  return 0;
                }}
              />
            </div>
          );
        }

        if (!record.weight) {
          return <div>-</div>;
        }
        return <div>{Math.round(record.weight[field] * 100)}%</div>;
      },
    };
  };

  const editButton = (
    <div
      style={{ position: 'relative', display: 'flex', gap: '10px', height: 30 }}
    >
      {!editing && (
        <Button
          onClick={() => {
            setEditing(true);
          }}
          style={{ position: 'absolute', right: 0 }}
        >
          Edit
        </Button>
      )}
      {editing && (
        <>
          <Button
            type="primary"
            onClick={() => {
              if (!isValid()) {
                return;
              }

              setEditing(false);
              dispatch(
                // updateAssetClassOptions({ asset_class_options: limits })
                updateLeaderboardWeights({ leaderboard_weights: weights })
              );
            }}
            style={{ position: 'absolute', right: 90 }}
          >
            Save
          </Button>
          <Button
            onClick={() => {
              setEditing(false);
              reset();
            }}
            style={{ position: 'absolute', right: 0 }}
          >
            Cancel
          </Button>
        </>
      )}
    </div>
  );

  const isValid = () => {
    const errorsList = [];

    const valid = Object.values(weights).reduce((acc, { weight }) => {
      return acc && weight >= 0 && weight <= 100;
    }, true);

    if (!valid) {
      errorsList.push('Weights must be between 0 and 100');
    }

    if (errorsList.length) {
      setErrors(errorsList);
      return false;
    }

    return true;
  };

  const columns = [
    {
      title: 'Metric',
      dataIndex: 'name',
    },
    {
      title: 'Weight',
      ...getProps('weight'),
    },
  ];

  const assetClassLimitsInfo = (
    <div>
      Specifying a limit on a certain asset class will enforce a hard limit on
      each instrument of that asset class.
    </div>
  );

  const noData = (
    <Empty
      description={
        <Typography.Text type={'secondary'}>
          Finish saving your instrument selection.
        </Typography.Text>
      }
    />
  );

  const noDataBehaviour = {
    emptyText: noData,
  };

  return (
    <Modal
      open={true}
      // onOk={form.submit}
      onCancel={hide}
      title={'Leaderboard Weights'}
      // footer={[{}]}
      width={600}
      footer={null}
    >
      <div>
        <Table
          rowKey="assetGroup"
          dataSource={dataSource}
          columns={columns}
          locale={noDataBehaviour}
          pagination={false}
          bordered
          footer={() => editButton}
        />
      </div>
    </Modal>
  );
};
export default EditWeightsModal;
